import { LEVEL_WIDTH, TileData, TileType } from "./types";
import { tileKey, type EnemySpawn, type LevelData } from "./level";

// World 2-2: underground, mostly ground with many gaps and brick formations
export function buildLevel2_2(): LevelData {
  const tiles = new Map<string, TileData>();
  const groundTop = -1;
  const groundDepth = 2;

  const pits: Array<[number, number]> = [
    [13, 15],
    [30, 34],
    [52, 55],
    [70, 73],
    [88, 91],
    [106, 110],
    [124, 127]
  ];

  const setTile = (x: number, y: number, type: TileType, payload?: TileData["payload"], flower?: boolean): void => {
    tiles.set(tileKey(x, y), { type, payload, broken: false, bump: 0, flower: flower || undefined });
  };

  const isPit = (x: number): boolean => pits.some(([a, b]) => x >= a && x <= b);

  for (let x = 0; x < LEVEL_WIDTH; x++) {
    if (isPit(x)) continue;
    for (let dy = 0; dy < groundDepth; dy++) {
      setTile(x, groundTop - dy, TileType.Ground);
    }
  }

  const stair = (x: number, h: number): void => {
    for (let dy = 0; dy < h; dy++) setTile(x, groundTop + 1 + dy, TileType.Block);
  };

  // early ? mushroom
  setTile(7, 4, TileType.Question, { kind: "mushroom" });

  // early blocks
  setTile(18, 4, TileType.Brick);
  setTile(19, 4, TileType.Brick);
  setTile(20, 4, TileType.Question, { kind: "coin" });
  setTile(21, 4, TileType.Brick);
  setTile(22, 4, TileType.Brick);

  // pipes
  const pipes: Array<[number, number]> = [
    [25, 2],
    [38, 3],
    [44, 4],
    [59, 2],
    [76, 3],
    [82, 4],
    [94, 2],
    [112, 3],
    [118, 4],
    [130, 2],
    [140, 3],
    [152, 4],
    [168, 2],
    [176, 3]
  ];
  for (const [px, h] of pipes) {
    for (let dy = 0; dy < h; dy++) {
      setTile(px, groundTop + 1 + dy, TileType.Pipe);
      setTile(px + 1, groundTop + 1 + dy, TileType.Pipe);
    }
  }

  // high coins
  setTile(29, 7, TileType.Question, { kind: "coin" });
  setTile(42, 8, TileType.Question, { kind: "coin" });
  setTile(64, 7, TileType.Question, { kind: "mushroom" });
  setTile(98, 7, TileType.Question, { kind: "coin" });
  setTile(122, 7, TileType.Question, { kind: "coin" });

  // brick structures
  for (let x = 132; x <= 138; x++) setTile(x, 4, TileType.Brick);
  setTile(134, 4, TileType.Question, { kind: "coin" });
  setTile(136, 4, TileType.Question, { kind: "coin" });
  setTile(130, 7, TileType.Brick);
  setTile(131, 7, TileType.Brick);
  setTile(132, 7, TileType.Brick);
  setTile(133, 7, TileType.Brick);
  setTile(134, 7, TileType.Brick);
  setTile(135, 7, TileType.Brick);
  setTile(136, 7, TileType.Brick);
  setTile(137, 7, TileType.Brick);
  setTile(138, 7, TileType.Brick);

  for (let x = 144; x <= 150; x++) setTile(x, 4, TileType.Brick);
  setTile(146, 4, TileType.Question, { kind: "mushroom" });
  setTile(148, 4, TileType.Question, { kind: "coin" });
  for (let x = 147; x <= 149; x++) setTile(x, 7, TileType.Brick);
  setTile(148, 7, TileType.Question, { kind: "coin" });

  setTile(156, 4, TileType.Brick);
  setTile(157, 4, TileType.Brick);
  setTile(158, 4, TileType.Brick);
  setTile(159, 4, TileType.Brick);
  setTile(160, 4, TileType.Question, { kind: "coin" });
  setTile(161, 4, TileType.Brick);

  // staircase 1
  stair(84, 1);
  stair(85, 2);
  stair(86, 3);
  stair(87, 4);
  stair(88, 4);
  stair(89, 4);
  stair(90, 3);
  stair(91, 2);
  stair(92, 1);

  // staircase 2
  stair(114, 1);
  stair(115, 2);
  stair(116, 3);
  stair(117, 4);
  stair(118, 4);
  stair(119, 4);
  stair(120, 3);
  stair(121, 2);
  stair(122, 1);

  // final staircase up
  stair(181, 1);
  stair(182, 2);
  stair(183, 3);
  stair(184, 4);
  stair(185, 4);
  stair(186, 4);
  stair(187, 4);
  stair(188, 3);
  stair(189, 2);
  stair(190, 1);

  const flagX = 198;
  for (let y = groundTop + 1; y <= 21; y++) setTile(flagX, y, TileType.Pole);
  setTile(flagX, 22, TileType.Block);

  const castleX = 202;

  const enemies: EnemySpawn[] = [
    { kind: "goomba", x: 10 },
    { kind: "goomba", x: 14 },
    { kind: "koopa", x: 20 },
    { kind: "goomba", x: 27 },
    { kind: "goomba", x: 32 },
    { kind: "goomba", x: 36 },
    { kind: "koopa", x: 40 },
    { kind: "goomba", x: 46 },
    { kind: "goomba", x: 49 },
    { kind: "goomba", x: 56 },
    { kind: "goomba", x: 60 },
    { kind: "koopa", x: 66 },
    { kind: "goomba", x: 70 },
    { kind: "goomba", x: 74 },
    { kind: "goomba", x: 78 },
    { kind: "goomba", x: 83 },
    { kind: "goomba", x: 87 },
    { kind: "koopa", x: 92 },
    { kind: "goomba", x: 96 },
    { kind: "goomba", x: 100 },
    { kind: "goomba", x: 104 },
    { kind: "goomba", x: 108 },
    { kind: "goomba", x: 112 },
    { kind: "koopa", x: 116 },
    { kind: "goomba", x: 120 },
    { kind: "goomba", x: 126 },
    { kind: "goomba", x: 130 },
    { kind: "goomba", x: 134 },
    { kind: "koopa", x: 140 },
    { kind: "goomba", x: 144 },
    { kind: "goomba", x: 148 },
    { kind: "goomba", x: 154 },
    { kind: "goomba", x: 158 },
    { kind: "koopa", x: 164 },
    { kind: "goomba", x: 170 },
    { kind: "goomba", x: 174 },
    { kind: "goomba", x: 180 }
  ];

  return { tiles, enemies, enterablePipes: [], flagX, castleX, width: LEVEL_WIDTH, groundY: 0 };
}
